import type { ModuleName, Permission } from "@dentacore/shared-types";
/**
 * The Settings rail — every destination, named, visible without a click.
 *
 * **This list is the fix for the complaint that Settings was not intuitive.**
 * Before it, the index named four things (Clinic details, Manage clinic, Google
 * Calendar, People) and five more were reachable only by opening a location and
 * scrolling: opening hours, chairs, procedures, prices and block reasons. An
 * admin who wanted to change a price had to already know that prices belong to
 * a branch — which is the database's shape (`DATA-MODEL.md` §1), not the shape
 * of the question they arrived with.
 *
 * So the grouping below is **by what somebody is trying to change**, and the
 * two-level clinic → branch structure is no longer what the reader navigates.
 * It is still what the *data* does; it is just not their problem any more.
 *
 * ── Why "Manage clinic" survives, on a group rather than a page ────────────
 *
 * It is the client's own wording, from QA item 10: *"in settings to being able
 * to modify or change anything (chairs...) it shouldnt be under locations, it
 * should be called manage clinic"*. The old screen honoured that by renaming a
 * heading whose children were still a list of addresses, which fixed the label
 * and not the problem. Here it labels the four things the sentence actually
 * names — the rooms, the hours, the procedures and the block reasons — with the
 * location they belong to chosen by the switcher above them rather than drilled
 * into.
 *
 * ── What is deliberately absent ───────────────────────────────────────────
 *
 * **There is no WhatsApp entry.** The redesign proposal showed one, and it was
 * wrong to: nothing clinic-level exists for WhatsApp. The only WhatsApp setting
 * in the product is the per-person assistant number on a person's own page, and
 * a rail item leading to an empty pane is worse than no rail item. It arrives
 * with Phase 5.
 */

/** A rail destination. `to` is relative to `/settings`. */
export interface SettingsNavItem {
  /** Path segment under `/settings`. */
  to: string;
  label: string;
  /**
   * Extra words this item should match when the rail is filtered, for the
   * things people call it that are not its label. Never rendered.
   *
   * "Room" is the kind that matters: somebody types what they call a thing,
   * not what this screen calls it.
   */
  also?: readonly string[];
  /**
   * Shown to whoever holds at least one of these (STORY-166). Presentation:
   * the pane's own routes refuse anyone else whatever the rail shows.
   */
  needs: readonly Permission[];
  /** The add-on this item belongs to; hidden without it (QF-D9, MS-D5). */
  module?: ModuleName;
}

export interface SettingsNavGroup {
  label: string;
  items: readonly SettingsNavItem[];
}

export const SETTINGS_NAV: readonly SettingsNavGroup[] = [
  {
    label: "Clinic",
    items: [
      {
        to: "practice",
        label: "Practice details",
        needs: ["settings.clinic.manage"],
        also: ["name", "logo", "subdomain", "currency", "timezone", "address", "phone", "email"],
      },
      {
        to: "locations",
        label: "Locations",
        also: ["branch", "branches", "close", "open"],
        needs: ["settings.locations.manage"],
      },
      // STORY-188b (MOD-D11): what the clinic has, and asking for the rest.
      {
        to: "modules",
        label: "Modules",
        also: ["add-on", "addon", "supply", "accounting", "chatbot", "plan", "subscription"],
        needs: ["settings.clinic.manage"],
      },
      // STORY-181f (HR-D7): how early certificates and probations are flagged.
      {
        to: "hr-warnings",
        label: "Expiry warnings",
        also: [
          "hr",
          "certificate",
          "certificates",
          "expiry",
          "probation",
          "warning",
          "window",
          "renewal",
        ],
        needs: ["settings.clinic.manage"],
      },
    ],
  },
  {
    label: "Manage clinic",
    items: [
      {
        to: "hours",
        label: "Opening hours",
        also: ["opening", "week", "closed", "day"],
        needs: ["settings.locations.manage"],
      },
      {
        to: "chairs",
        label: "Chairs",
        also: ["room", "rooms", "operatory", "column"],
        needs: ["settings.locations.manage"],
      },
      {
        to: "block-reasons",
        label: "Block reasons",
        also: ["buffer", "maintenance", "held"],
        needs: ["settings.locations.manage"],
      },
      // EB5 — the automatic WhatsApp reminder had no screen at all.
      {
        to: "reminders",
        label: "Reminders",
        also: ["whatsapp", "remind", "message", "evening"],
        needs: ["settings.locations.manage"],
        // Reminders go out over WhatsApp — Chatbot's (MOD-D16).
        module: "CHATBOT",
      },
      // STORY-177 — the day grid's hours and zoom, per location.
      {
        to: "calendar-view",
        label: "Calendar",
        also: ["hours", "zoom", "size", "scale", "view", "start", "end", "8", "20"],
        needs: ["settings.locations.manage"],
      },
    ],
  },
  {
    // Renamed from "People" (Emile, 2026-10-22).
    label: "Roles and Permissions",
    items: [
      // Staff moved to People → Staff (MS-D3); System keeps Roles and access.
      // STORY-167: the clinic's roles, what each carries, and the overrides.
      {
        to: "access",
        label: "Roles and access",
        also: ["role", "roles", "permission", "permissions", "override", "access"],
        needs: ["access.roles.manage", "access.assign"],
      },
    ],
  },
  {
    label: "Integrations",
    items: [
      {
        to: "google",
        label: "Google Calendar",
        also: ["sync", "calendar", "gcal"],
        needs: ["settings.integrations.manage"],
      },
    ],
  },
];

/** Where `/settings` lands. The first item of the first group, by construction. */
export const SETTINGS_HOME = "practice";

/**
 * Does this item match what somebody typed into the rail's filter?
 *
 * Matches the label and the `also` words, case-insensitively, on a substring —
 * so "room", "Rooms" and "operatory" all reach Chairs.
 */
export function navItemMatches(item: SettingsNavItem, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  if (item.label.toLowerCase().includes(needle)) return true;
  return (item.also ?? []).some((word) => word.toLowerCase().includes(needle));
}
